import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import type { Fleet } from '../lib/types'
import { Icon } from './Icon'
import { Button, Field, Modal, Spinner, toast } from './ui'

/**
 * Fleets group an account's vehicles: the Vehicles and Live pages filter by
 * them, reports run per fleet, and a user can be restricted to some of them.
 * The API has always had them; this is the first place they can be created,
 * renamed and removed, so the "Fleet" dropdown on a vehicle has something in
 * it. Fleets belong to the account you are signed in to (or logged in as).
 */
export function FleetManager({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const fleets = useQuery({ queryKey: ['fleets', ''], queryFn: () => api<{ items: Fleet[] }>('/fleets') })

  const [newName, setNewName] = useState('')
  const [newDescription, setNewDescription] = useState('')
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null)

  // Every fleet list in the app, whichever account it was fetched for.
  const refresh = () => void qc.invalidateQueries({ queryKey: ['fleets'] })

  const create = useMutation({
    mutationFn: () =>
      api<Fleet>('/fleets', { body: { name: newName.trim(), description: newDescription.trim() } }),
    onSuccess: (f) => {
      toast(`Fleet "${f.name}" created`, 'ok')
      setNewName('')
      setNewDescription('')
      refresh()
    },
    onError: (e: Error) => toast(e.message, 'error'),
  })

  const rename = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api(`/fleets/${id}`, { method: 'PATCH', body: { name: name.trim() } }),
    onSuccess: () => {
      toast('Fleet renamed', 'ok')
      setRenaming(null)
      refresh()
    },
    onError: (e: Error) => toast(e.message, 'error'),
  })

  const remove = useMutation({
    mutationFn: (id: string) => api(`/fleets/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast('Fleet removed', 'ok')
      refresh()
    },
    onError: (e: Error) => toast(e.message, 'error'),
  })

  const items = fleets.data?.items ?? []

  return (
    <Modal
      open
      onClose={onClose}
      title="Fleets"
      width={560}
      footer={<Button onClick={onClose}>Done</Button>}
    >
      <div className="space-y-4">
        <p className="text-[12.5px] text-[var(--color-ink-2)] leading-relaxed">
          Fleets group this account's vehicles — for filtering the map and lists, running reports per fleet,
          and limiting what a user may see. A vehicle joins a fleet on its own edit form.
        </p>

        {/* Add */}
        <div className="rounded-[3px] p-3" style={{ background: 'var(--color-panel-2)', border: '1px solid var(--color-rule)' }}>
          <div className="label mb-2 text-[10px]">New fleet</div>
          <div className="grid sm:grid-cols-[1fr_1.4fr_auto] gap-2 items-end">
            <Field label="Name" required>
              <input
                className="field"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Gauteng deliveries"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newName.trim()) create.mutate()
                }}
              />
            </Field>
            <Field label="Description">
              <input
                className="field"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="Optional"
              />
            </Field>
            <Button variant="primary" disabled={!newName.trim() || create.isPending} onClick={() => create.mutate()}>
              <Icon name="plus" size={13} />
              {create.isPending ? 'Adding…' : 'Add'}
            </Button>
          </div>
        </div>

        {/* List */}
        {fleets.isLoading ? (
          <div className="py-6 flex justify-center">
            <Spinner />
          </div>
        ) : items.length === 0 ? (
          <p className="text-[12.5px] text-[var(--color-ink-3)]">No fleets yet — add the first one above.</p>
        ) : (
          <ul className="rounded-[3px] overflow-hidden" style={{ border: '1px solid var(--color-rule)' }}>
            {items.map((f) => (
              <li key={f.id} className="flex items-center gap-3 px-3 py-2 rule-b last:border-b-0">
                {renaming?.id === f.id ? (
                  <input
                    className="field grow"
                    autoFocus
                    value={renaming.name}
                    onChange={(e) => setRenaming({ id: f.id, name: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && renaming.name.trim()) rename.mutate(renaming)
                      if (e.key === 'Escape') setRenaming(null)
                    }}
                  />
                ) : (
                  <div className="min-w-0 grow">
                    <div className="text-[13px] text-[var(--color-ink)] truncate">{f.name}</div>
                    <div className="text-[11px] text-[var(--color-ink-4)] truncate">
                      {[f.description, `${f.vehicleCount} vehicle${f.vehicleCount === 1 ? '' : 's'}`]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  </div>
                )}

                {renaming?.id === f.id ? (
                  <>
                    <Button variant="primary" disabled={!renaming.name.trim() || rename.isPending} onClick={() => rename.mutate(renaming)}>
                      Save
                    </Button>
                    <Button onClick={() => setRenaming(null)}>Cancel</Button>
                  </>
                ) : (
                  <div className="flex gap-1 shrink-0">
                    <button className="btn btn-ghost btn-sm px-1.5" title="Rename" onClick={() => setRenaming({ id: f.id, name: f.name })}>
                      <Icon name="edit" size={13} />
                    </button>
                    <button
                      className="btn btn-ghost btn-sm px-1.5"
                      title={f.vehicleCount ? 'Remove fleet (its vehicles become unassigned)' : 'Remove fleet'}
                      style={{ color: 'var(--text-critical)' }}
                      onClick={() => {
                        const note = f.vehicleCount
                          ? ` Its ${f.vehicleCount} vehicle${f.vehicleCount === 1 ? '' : 's'} will become unassigned.`
                          : ''
                        if (confirm(`Remove fleet "${f.name}"?${note}`)) remove.mutate(f.id)
                      }}
                    >
                      <Icon name="trash" size={13} />
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  )
}
